import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {message} from 'antd';
import {AxiosError, AxiosResponse} from 'axios';
import {ADD_BATTERY, BATTERY_DATA, UNREGISTERED, UPDATE_BATTERY} from './batteries.type';
import type FILTERS from '../../../../common/filters.type';

import batteriesServices from './batteriesServices';

const initialState = {
  isLoading: false,
  batteries: {
    page_size: 10,
    page_number: 1,
    sort_by: 'asset_id',
    ascending: true,
    search: '',
    totalDataCount: 0,
    is_active: undefined as boolean | undefined,
    pageData: [] as BATTERY_DATA[],
  },
  unregistered: [] as UNREGISTERED[],
};

export const batteriesList = createAsyncThunk(
  'batteriesList',
  async (payload: FILTERS, thunkAPI) => {
    try {
      const data: AxiosResponse = await batteriesServices.batteriesList(payload);
      return data;
    } catch (error) {
      const result = error as AxiosError;
      return thunkAPI.rejectWithValue(result.message);
    }
  },
);
export const unregisteredBatteries = createAsyncThunk(
  'unregisteredBatteries',
  async (rack_id: number, thunkAPI) => {
    try {
      const data: AxiosResponse = await batteriesServices.unregisteredBatteries(rack_id);
      return data;
    } catch (error) {
      const result = error as AxiosError;
      return thunkAPI.rejectWithValue(result.message);
    }
  },
);

export const addBattery = createAsyncThunk('addBattery', async (values: ADD_BATTERY, thunkAPI) => {
  try {
    const data: AxiosResponse = await batteriesServices.addBattery(values);
    return data;
  } catch (error) {
    const result = error as AxiosError;
    return thunkAPI.rejectWithValue(result.message);
  }
});
export const updateBattery = createAsyncThunk(
  'updateBattery',
  async (
    values: {
      payload: UPDATE_BATTERY;
      id: number;
    },
    thunkAPI,
  ) => {
    try {
      const data: AxiosResponse = await batteriesServices.updateBattery({
        payload: values.payload,
        id: values.id,
      });
      return data;
    } catch (error) {
      const result = error as AxiosError;
      return thunkAPI.rejectWithValue(result.message);
    }
  },
);

export const batteriesSlice = createSlice({
  name: 'batteries',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    pageNumberChange(state, action: PayloadAction<number>) {
      state.batteries.page_number = action.payload;
    },
    pageSizeChange(state, action: PayloadAction<number>) {
      state.batteries.page_number = 1;
      state.batteries.page_size = action.payload;
    },
    searchBy(state, action: PayloadAction<string>) {
      state.batteries.page_number = 1;
      state.batteries.search = action.payload;
    },
    sortBy(state, action: PayloadAction<{ascending: boolean; sortBy: string}>) {
      state.batteries.ascending = action.payload.ascending;
      state.batteries.sort_by = action.payload.sortBy;
    },
    batteriesStatusFilter(state, action: PayloadAction<boolean | undefined>) {
      state.batteries.page_number = 1;
      state.batteries.is_active = action.payload;
    },
    clearBatteriesFilters(state) {
      state.batteries.page_size = 10;
      state.batteries.page_number = 1;
      state.batteries.search = '';
      state.batteries.sort_by = 'asset_id';
      state.batteries.is_active = undefined;
    },
    clearUnregistered(state) {
      state.unregistered = [];
    },
  },

  extraReducers: (builder) => {
    builder.addCase(batteriesList.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(batteriesList.fulfilled, (state, action) => {
      state.isLoading = false;
      state.batteries = action.payload.data;
    });
    builder.addCase(batteriesList.rejected, (state, action) => {
      state.isLoading = false;
      state.batteries = initialState.batteries;
      message.error(`${action.payload}`);
    });

    builder.addCase(addBattery.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(addBattery.fulfilled, (state) => {
      state.isLoading = false;
      message.success(`Battery added Successfully`);
    });
    builder.addCase(addBattery.rejected, (state, action) => {
      state.isLoading = false;
      message.error(`${action.payload}`);
    });

    builder.addCase(updateBattery.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(updateBattery.fulfilled, (state) => {
      state.isLoading = false;
      message.success(`Battery updated Successfully`);
    });
    builder.addCase(updateBattery.rejected, (state, action) => {
      state.isLoading = false;
      message.error(`${action.payload}`);
    });

    builder.addCase(unregisteredBatteries.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(unregisteredBatteries.fulfilled, (state, action) => {
      state.isLoading = false;
      state.unregistered = action.payload.data;
    });
    builder.addCase(unregisteredBatteries.rejected, (state, action) => {
      state.isLoading = false;
      message.error(`${action.payload}`);
    });
  },
});
export const {
  pageNumberChange,
  pageSizeChange,
  searchBy,
  sortBy,
  batteriesStatusFilter,
  clearUnregistered,
  clearBatteriesFilters,
} = batteriesSlice.actions;

export default batteriesSlice.reducer;
